import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ClientDropdown from '../components/ClientDropdown';
import { useCanCreate } from '../components/Can';
import BoxScanner from '../components/BoxScanner';
import { useCompanyScope } from '../hooks/useCompanyScope';
import CompanyCell from '../components/CompanyCell';
import CompanySelect from '../components/CompanySelect';

const errMsg = (e: any) => e?.response?.data?.message || e?.message || 'Something went wrong';
const fmt = (n: number) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

interface Box { yarnMasterItemId: number; boxNo: string; netWeight: number; location?: string; yarnName?: string; lot?: string; }
interface SelectedBox extends Box { weight: string; }

const STATUS_STYLE: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  REJECTED: 'bg-red-50 text-red-700 border-red-200',
};
// Flow: an employee submits a request (PENDING); an admin approves it (APPROVED → stock deducted)
// or rejects it (REJECTED, nothing deducted).
const STATUS_LABEL: Record<string, string> = { PENDING: 'Pending approval', APPROVED: 'Approved', REJECTED: 'Rejected' };
// Left accent bar colour per status (for the entry cards).
const STATUS_ACCENT: Record<string, string> = { PENDING: 'bg-amber-400', APPROVED: 'bg-emerald-500', REJECTED: 'bg-red-500' };
const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md border ${STATUS_STYLE[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>{STATUS_LABEL[status] || status}</span>
);

// Searchable dropdown (buyer / box). Renders options on focus, filter by typing, click/Enter to pick.
// With `multiple`, rows become checkboxes and a footer commits the whole batch at once.
const SearchSelect: React.FC<{
  value: string;
  placeholder: string;
  options: { value: string; label: string; meta?: string }[];
  onPick: (value: string) => void;
  emptyText?: string;
  widthClass?: string;
  /** Tick several rows and add them in one go. */
  multiple?: boolean;
  onPickMany?: (values: string[]) => void;
}> = ({ value, placeholder, options, onPick, emptyText = 'No matches', widthClass = 'w-full', multiple, onPickMany }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [ticked, setTicked] = useState<string[]>([]);
  const [pos, setPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ top: 0, left: 0, width: 240 });
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))) {
        setIsOpen(false);
        setTicked([]); // dismissing discards an uncommitted batch
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  useEffect(() => {
    if (!isOpen || !inputRef.current) return;
    const reposition = () => {
      if (!inputRef.current) return;
      const r = inputRef.current.getBoundingClientRect();
      // Flip above the field when the viewport (or an on-screen keyboard) squeezes the space below.
      const shouldOpenUp = window.innerHeight - r.bottom < 240 && r.top > 240;
      setOpenUp(shouldOpenUp);
      const width = Math.min(Math.max(r.width, 240), window.innerWidth - 16);
      setPos({
        top: shouldOpenUp ? undefined : r.bottom + 2,
        bottom: shouldOpenUp ? (window.innerHeight - r.top + 2) : undefined,
        left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
        width,
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const term = (isOpen ? search : value).toLowerCase();
  const filtered = options.filter((o) => o.label.toLowerCase().includes(term) || (o.meta || '').toLowerCase().includes(term));
  const pick = (v: string) => {
    onPick(v);
    setSearch('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const shown = filtered.slice(0, 100);
  const toggle = (v: string) => setTicked((t) => (t.includes(v) ? t.filter((x) => x !== v) : [...t, v]));
  const allShownTicked = shown.length > 0 && shown.every((o) => ticked.includes(o.value));
  const toggleAllShown = () => setTicked((t) => (allShownTicked
    ? t.filter((v) => !shown.some((o) => o.value === v))
    : Array.from(new Set([...t, ...shown.map((o) => o.value)]))));
  // Adding a batch is an explicit "done" — close and drop the keyboard.
  const commit = () => {
    if (!ticked.length) return;
    onPickMany?.(ticked);
    setTicked([]);
    setSearch('');
    setIsOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div className={`relative ${widthClass}`} ref={ref}>
      <input ref={inputRef} type="text" value={isOpen ? search : value}
        onChange={(e) => { setSearch(e.target.value); if (!isOpen) setIsOpen(true); }}
        onFocus={() => { setIsOpen(true); setSearch(''); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && filtered[0]) {
            e.preventDefault();
            if (multiple) { ticked.length ? commit() : toggle(filtered[0].value); } else pick(filtered[0].value);
          } else if (e.key === 'Escape') { setIsOpen(false); setTicked([]); }
        }}
        placeholder={placeholder}
        className="w-full px-2.5 py-1.5 text-sm border border-[#E1E6EF] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0055a9]/20 focus:border-[#0055a9]" />
      {isOpen && createPortal(
        <div ref={dropdownRef} className="fixed z-[9999] bg-white border border-[#E1E6EF] rounded-xl shadow-[0px_4px_16px_rgba(0,0,0,0.1)] flex flex-col overflow-hidden"
          style={{ ...(openUp ? { bottom: pos.bottom } : { top: pos.top }), left: pos.left, width: pos.width }}>
          <div className="overflow-y-auto max-h-60 overscroll-contain">
            {shown.length ? shown.map((o) => {
              const on = ticked.includes(o.value);
              return (
                <button key={o.value} type="button" tabIndex={-1} onClick={() => (multiple ? toggle(o.value) : pick(o.value))}
                  className={`w-full flex items-center gap-2.5 text-left px-3 py-2 text-sm transition-colors ${on ? 'bg-[#0055a9]/5' : 'hover:bg-[#F8F9FC]'}`}>
                  {multiple && (
                    <span className={`flex-shrink-0 w-[18px] h-[18px] rounded-[5px] border flex items-center justify-center transition-colors ${
                      on ? 'bg-[#0055a9] border-[#0055a9]' : 'bg-white border-[#C7CEDB]'}`}>
                      {on && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-[#0E131B]">{o.label}</span>
                    {o.meta && <span className="block text-[11px] text-[rgba(14,19,27,0.45)]">{o.meta}</span>}
                  </span>
                </button>
              );
            }) : <div className="px-3 py-2 text-xs text-[rgba(14,19,27,0.4)]">{emptyText}</div>}
          </div>
          {multiple && shown.length > 0 && (
            <div className="flex items-center gap-2 px-2 py-2 border-t border-[#E1E6EF] bg-[#F8F9FC]">
              <button type="button" tabIndex={-1} onClick={toggleAllShown}
                className="px-2.5 py-2 text-xs font-semibold text-[#0055a9] rounded-lg hover:bg-[#0055a9]/5">
                {allShownTicked ? 'Clear all' : `Select all (${shown.length})`}
              </button>
              <button type="button" tabIndex={-1} onClick={commit} disabled={!ticked.length}
                className="ml-auto px-3 py-2 text-xs font-bold text-white bg-[#0055a9] rounded-lg disabled:opacity-40 hover:bg-[#0d2780] transition-colors">
                Add{ticked.length ? ` ${ticked.length}` : ''}
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

const YarnOutward: React.FC = () => {
  const { user } = useAuth() as { user: any };
  const { showCompanyColumn } = useCompanyScope();
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  // Creating a request needs CREATE; approving it (which deducts stock) is an admin capability.
  const canCreate = useCanCreate('yarn-outward');
  const canApprove = user?.role === 'SUPERADMIN' || user?.role === 'ADMIN';
  // Land users on the tab that fits their role: creators on New Entry, approvers on Approvals.
  const [tab, setTab] = useState<'new' | 'mine' | 'approvals'>(canCreate ? 'new' : (canApprove ? 'approvals' : 'mine'));

  // ── New Request state ──
  const [buyer, setBuyer] = useState('');
  const [buyerClientId, setBuyerClientId] = useState<number | null>(null);
  const [challanNo, setChallanNo] = useState('');
  const [challanDate, setChallanDate] = useState(new Date().toISOString().split('T')[0]);
  const [selected, setSelected] = useState<SelectedBox[]>([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  // Refs so the (stable) scan handler always reads the latest buyer/selected without stale closures.
  const buyerRef = useRef(buyer); buyerRef.current = buyer;
  const selectedRef = useRef(selected); selectedRef.current = selected;

  // Same shape as Yarn Master's ['clients'] query (returns the array) so the shared cache stays consistent
  // other pages cache ['clients'] as the whole {clients:[...]} object — normalize to an array
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => { const d = await apiService.get('/clients'); return d.clients || []; }, select: (d: any) => (Array.isArray(d) ? d : (d?.clients || [])) });

  // Only buyers that currently have dispatchable yarn stock should appear in the buyer dropdown.
  const { data: buyersStockResp } = useQuery({ queryKey: ['buyersWithStock'], queryFn: async () => await apiService.get('/yarn-outwards/buyers-with-stock') });
  const buyerClients = useMemo(() => {
    const withStock = new Set((buyersStockResp?.buyers || []).map((b: string) => String(b).trim().toLowerCase()));
    return (Array.isArray(clients) ? clients : []).filter((c: any) => withStock.has(String(c.name || '').trim().toLowerCase()));
  }, [clients, buyersStockResp]);

  const { data: boxesResp, isFetching: boxesLoading } = useQuery({
    queryKey: ['availableBoxes', buyer],
    queryFn: async () => await apiService.get(`/yarn-outwards/available-boxes?buyer=${encodeURIComponent(buyer)}`),
    enabled: !!buyer,
  });
  const availableBoxes: Box[] = boxesResp?.boxes || [];
  const boxOptions = useMemo(() => availableBoxes
    .filter((b) => !selected.some((s) => s.yarnMasterItemId === b.yarnMasterItemId))
    .map((b) => ({ value: String(b.yarnMasterItemId), label: b.boxNo, meta: `${fmt(b.netWeight)} kg · ${b.location || '—'} · ${b.yarnName || ''}` })),
  [availableBoxes, selected]);

  // Create a new client on the fly from the buyer dropdown (same behaviour as Yarn Master)
  const createClient = useMutation({
    mutationFn: async (name: string) => await apiService.post('/clients', { name }),
    onSuccess: () => { toast.success('Client added'); queryClient.invalidateQueries({ queryKey: ['clients'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addBox = (idStr: string) => addBoxes([idStr]);
  const addBoxes = (idStrs: string[]) => {
    setSelected((prev) => {
      const have = new Set(prev.map((s) => s.yarnMasterItemId));
      const add = idStrs
        .map((id) => availableBoxes.find((x) => String(x.yarnMasterItemId) === id))
        .filter((b): b is Box => !!b && !have.has(b.yarnMasterItemId))
        .map((b) => ({ ...b, weight: String(b.netWeight ?? '') }));
      return add.length ? [...prev, ...add] : prev;
    });
  };
  const removeBox = (id: number) => setSelected((prev) => prev.filter((s) => s.yarnMasterItemId !== id));
  const setWeight = (id: number, w: string) => setSelected((prev) => prev.map((s) => (s.yarnMasterItemId === id ? { ...s, weight: w } : s)));

  // Scan a box QR → look it up, set the buyer on the first scan, and add the box. Rejects a box
  // from a different buyer, an already-added box, or an already-dispatched/unknown box.
  const handleScan = async (code: string) => {
    if (!code) return;
    try {
      const resp: any = await apiService.get(`/yarn-outwards/scan-box?code=${encodeURIComponent(code)}`);
      const box = resp?.box;
      if (!box) { toast.error('Box not found'); return; }
      if (selectedRef.current.some((s) => s.yarnMasterItemId === box.yarnMasterItemId)) {
        toast(`Box ${box.boxNo} is already added`);
        return;
      }
      const current = buyerRef.current;
      if (!current) {
        setBuyer(box.buyerName || '');
        setBuyerClientId(box.buyerClientId ?? null);
      } else if (String(box.buyerName || '').trim().toLowerCase() !== current.trim().toLowerCase()) {
        toast.error(`Box ${box.boxNo} belongs to ${box.buyerName} — finish this buyer first`);
        return;
      }
      setSelected((prev) => prev.some((s) => s.yarnMasterItemId === box.yarnMasterItemId)
        ? prev
        : [...prev, { yarnMasterItemId: box.yarnMasterItemId, boxNo: box.boxNo, netWeight: box.netWeight, location: box.location, yarnName: box.yarnName, lot: box.lot, weight: String(box.netWeight ?? '') }]);
      toast.success(`Box ${box.boxNo} added`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Scan failed');
    }
  };

  const totalNet = selected.reduce((s, b) => s + (Number(b.weight) || 0), 0);

  const submit = useMutation({
    mutationFn: async () => await apiService.post('/yarn-outwards', {
      buyerName: buyer, buyerClientId, challanNo, challanDate,
      items: selected.map((s) => ({ yarnMasterItemId: s.yarnMasterItemId, boxNo: s.boxNo, netWeight: Number(s.weight) || 0 })),
      tenantId: showCompanyColumn ? selectedTenantId : undefined,
    }),
    onSuccess: () => {
      toast.success('Request submitted — awaiting admin approval');
      setSelected([]); setChallanNo(''); setSelectedTenantId(null);
      queryClient.invalidateQueries({ queryKey: ['myOutwards'] });
      queryClient.invalidateQueries({ queryKey: ['availableBoxes'] });
      queryClient.invalidateQueries({ queryKey: ['outwardQueue'] });
      setTab('mine');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const handleSubmit = () => {
    if (!buyer.trim()) return toast.error('Select a buyer');
    if (!selected.length) return toast.error('Add at least one box');
    if (showCompanyColumn && !selectedTenantId) return toast.error('Select a company');
    submit.mutate();
  };

  // ── My Requests ──
  const { data: mineResp, isLoading: mineLoading } = useQuery({ queryKey: ['myOutwards'], queryFn: async () => await apiService.get('/yarn-outwards/mine') });
  const myOutwards: any[] = mineResp?.outwards || [];

  // ── Approvals ──
  const { data: queueResp, isLoading: queueLoading } = useQuery({
    queryKey: ['outwardQueue'],
    queryFn: async () => await apiService.get('/yarn-outwards?status=PENDING'),
    enabled: canApprove && tab === 'approvals',
  });
  const queue: any[] = queueResp?.outwards || [];

  // Admin decision: approve (deduct every box's weight from stock at once) or reject (nothing deducted).
  const review = useMutation({
    mutationFn: async ({ id, action, note }: { id: number; action: 'approve' | 'reject'; note?: string }) =>
      await apiService.post(`/yarn-outwards/${id}/${action}`, { note }),
    onSuccess: (_data: any, v) => {
      toast.success(v.action === 'approve' ? 'Approved — stock deducted' : 'Request rejected');
      queryClient.invalidateQueries({ queryKey: ['outwardQueue'] });
      queryClient.invalidateQueries({ queryKey: ['yarnDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['yarn-masters'] });
      queryClient.invalidateQueries({ queryKey: ['availableBoxes'] });
      queryClient.invalidateQueries({ queryKey: ['buyersWithStock'] });
      queryClient.invalidateQueries({ queryKey: ['myOutwards'] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  // Confirm dialog before an irreversible approve (deduct) or a reject.
  const [confirm, setConfirm] = useState<{ o: any; action: 'approve' | 'reject' } | null>(null);
  // Which My-Entries cards are expanded to show their boxes.
  const [openEntries, setOpenEntries] = useState<Record<number, boolean>>({});

  const TabButton: React.FC<{ id: typeof tab; label: string; count?: number }> = ({ id, label, count }) => (
    <button onClick={() => setTab(id)}
      className={`flex-shrink-0 whitespace-nowrap px-3 sm:px-4 py-2 text-sm font-semibold rounded-xl transition-colors ${tab === id ? 'bg-[#0055a9] text-white' : 'text-gray-600 bg-white border border-[#E1E6EF] hover:bg-[#F8F9FC]'}`}>
      {label}{typeof count === 'number' && count > 0 && <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${tab === id ? 'bg-white/25' : 'bg-[#0055a9]/10 text-[#0055a9]'}`}>{count}</span>}
    </button>
  );

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} className="px-0 sm:px-2 py-3 sm:py-4 space-y-4">
      {/* Page name is already shown in the top bar — no duplicate heading here. */}
      {/* Tab strip scrolls sideways on narrow screens rather than wrapping mid-row */}
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1 sm:mx-0 sm:px-0 sm:flex-wrap sm:overflow-visible">
        {canCreate && <TabButton id="new" label="New Entry" />}
        {(canCreate || !canApprove) && <TabButton id="mine" label="My Entries" count={myOutwards.filter((o) => o.status === 'PENDING').length} />}
        {canApprove && <TabButton id="approvals" label="Approvals" count={queue.length} />}
      </div>

      {/* ── NEW REQUEST ── */}
      {tab === 'new' && (
        <div className="space-y-4 pb-2">
          {showCompanyColumn && (
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-[rgba(14,19,27,0.45)] mb-1.5">Company</p>
              <div className="bg-white rounded-xl border border-[#E8EBF1] px-4 py-3">
                <CompanySelect value={selectedTenantId} onChange={setSelectedTenantId} />
              </div>
            </div>
          )}
          {/* STEP 1 — Buyer + challan */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wider text-[rgba(14,19,27,0.45)] mb-1.5">Step 1 · Who is it going to?</p>
            <div className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
              {buyer ? (
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EEF0F3]">
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Buyer</p>
                    <p className="text-lg font-bold text-[#0E131B] truncate mt-0.5">{buyer}</p>
                  </div>
                  <button type="button" onClick={() => { setBuyer(''); setBuyerClientId(null); setSelected([]); }}
                    className="shrink-0 text-sm font-semibold text-[#0055a9] hover:text-[#0d2780]">Change</button>
                </div>
              ) : (
                <div className="px-4 py-3 border-b border-[#EEF0F3]">
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)] mb-1.5">Buyer *</label>
                  <ClientDropdown
                    value={buyer}
                    clients={buyerClients}
                    onChange={(name, clientId) => { setBuyer(name); setBuyerClientId(clientId ?? null); setSelected([]); }}
                    onCreateClient={(name) => createClient.mutate(name)}
                  />
                </div>
              )}
              <div className="grid grid-cols-2">
                <div className="px-4 py-2.5 border-r border-[#EEF0F3]">
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Challan No</label>
                  <input type="text" value={challanNo} onChange={(e) => setChallanNo(e.target.value)} placeholder="OUT-101"
                    className="w-full mt-0.5 text-sm font-mono bg-transparent focus:outline-none placeholder:text-[rgba(14,19,27,0.3)]" />
                </div>
                <div className="px-4 py-2.5">
                  <label className="block text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Date</label>
                  <input type="date" value={challanDate} onChange={(e) => setChallanDate(e.target.value)}
                    className="w-full mt-0.5 text-sm font-mono bg-transparent focus:outline-none" />
                </div>
              </div>
            </div>
          </div>

          {/* STEP 2 — Scan boxes */}
          <div>
            <div className="flex items-baseline justify-between gap-2 mb-1.5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Step 2 · Scan the boxes</p>
              {buyer && <p className="text-[11px] font-mono text-[rgba(14,19,27,0.5)]">{boxesLoading ? 'loading…' : `${availableBoxes.length} in stock`}</p>}
            </div>
            {canCreate && (
              <button type="button" onClick={() => setScannerOpen(true)}
                className="w-full h-16 bg-[#0055a9] text-white rounded-xl flex items-center justify-center gap-3 hover:bg-[#0d2780] transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                <span className="text-lg font-bold">Scan Box</span>
              </button>
            )}
            {!buyer ? (
              <p className="mt-2 text-xs text-[rgba(14,19,27,0.4)]">Select a buyer first to load their in-stock boxes.</p>
            ) : (
              <div className="mt-2">
                <SearchSelect value="" placeholder={boxOptions.length ? 'Type or search box no. instead…' : 'No more boxes available'} options={boxOptions} onPick={addBox}
                  emptyText={availableBoxes.length ? 'All in-stock boxes have been added' : 'No in-stock boxes for this buyer'}
                  widthClass="w-full" multiple onPickMany={addBoxes} />
              </div>
            )}
          </div>

          <BoxScanner open={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScan} />

          {/* Scanned boxes list */}
          <div className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-[#F8F9FC] border-b border-[#E8EBF1]">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[rgba(14,19,27,0.6)]">Scanned boxes</p>
              <span className="ml-auto text-[11px] font-mono text-[rgba(14,19,27,0.5)]">{selected.length === 1 ? '1 box' : `${selected.length} boxes`}</span>
            </div>
            {selected.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm font-semibold text-[#0E131B]">No boxes scanned yet</p>
                <p className="text-xs text-[rgba(14,19,27,0.5)] mt-1 leading-relaxed max-w-xs mx-auto">Press the blue Scan Box button above. Each box you scan is added here with its weight.</p>
              </div>
            ) : (
              selected.map((b) => (
                <div key={b.yarnMasterItemId} className="flex items-center gap-3 px-4 py-3 border-b border-[#EEF0F3] last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-mono font-semibold text-[#0055a9]">{b.boxNo}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">Added</span>
                    </div>
                    {(b.yarnName || b.location) && <p className="text-xs text-[rgba(14,19,27,0.5)] mt-0.5 truncate">{b.yarnName || '—'}{b.location ? ` · Bin ${b.location}` : ''}</p>}
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <input type="number" step="0.01" inputMode="decimal" value={b.weight} onChange={(e) => setWeight(b.yarnMasterItemId, e.target.value)}
                      className="w-[72px] px-2 py-1.5 text-sm text-right font-mono border border-[#E1E6EF] rounded-lg focus:outline-none focus:border-[#0055a9] focus:ring-2 focus:ring-[#0055a9]/20 tabular-nums" />
                    <span className="text-[10px] font-mono text-[rgba(14,19,27,0.4)]">kg</span>
                  </div>
                  <button type="button" onClick={() => removeBox(b.yarnMasterItemId)} aria-label={`Remove box ${b.boxNo}`}
                    className="shrink-0 h-9 w-9 rounded-lg border border-red-100 text-red-500 flex items-center justify-center hover:bg-red-50 transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Hint */}
          <div className="px-3.5 py-3 bg-[#F8F9FC] border-l-[3px] border-[rgba(14,19,27,0.3)] rounded-r-lg text-xs text-[rgba(14,19,27,0.6)] leading-relaxed">
            Weights come from the box label. Change one only if the scale reading is different — the change is recorded against your name.
          </div>

          {/* Total + submit bar */}
          <div className="bg-[#14181d] rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/50">Total</span>
              <span className="text-2xl font-mono font-bold text-white tabular-nums">{fmt(totalNet)}</span>
              <span className="text-[11px] font-mono text-white/55">kg · {selected.length === 1 ? '1 box' : `${selected.length} boxes`}</span>
            </div>
            <button onClick={handleSubmit} disabled={submit.isPending || !canCreate || !selected.length}
              title={!canCreate ? 'You need create access' : undefined}
              className="ml-auto w-full sm:w-auto px-5 py-3 text-sm font-bold text-white bg-[#0055a9] rounded-lg hover:bg-[#0d2780] disabled:opacity-40 disabled:cursor-not-allowed">
              {submit.isPending ? 'Submitting…' : (selected.length ? `Submit ${selected.length} box${selected.length > 1 ? 'es' : ''} for approval` : 'Scan a box to continue')}
            </button>
          </div>
        </div>
      )}

      {/* ── MY ENTRIES (collapsible cards) ── */}
      {tab === 'mine' && (
        <div className="space-y-3">
          {mineLoading ? <div className="p-10 text-center"><div className="animate-spin rounded-full h-7 w-7 border-2 border-[#0055a9] border-t-transparent mx-auto" /></div>
            : myOutwards.length === 0 ? <div className="bg-white rounded-xl border border-[#E8EBF1] p-10 text-center text-sm text-[rgba(14,19,27,0.5)]">No entries yet.</div>
            : myOutwards.map((o) => {
                const items = o.items || [];
                const isOpen = !!openEntries[o.id];
                return (
                  <div key={o.id} className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
                    <button onClick={() => setOpenEntries((p) => ({ ...p, [o.id]: !p[o.id] }))}
                      className="w-full flex items-stretch gap-3 px-3 py-3 text-left hover:bg-[#FAFBFD] transition-colors">
                      <span className={`w-1 rounded-full shrink-0 ${STATUS_ACCENT[o.status] || 'bg-gray-300'}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-base font-bold text-[#1B202A] truncate">{o.buyerName}</p>
                        <p className="text-[11px] font-mono text-[rgba(14,19,27,0.5)] mt-0.5">
                          {o.challanDate || ''}{o.challanNo ? ` · ${o.challanNo}` : ''} · {items.length} box
                        </p>
                        {showCompanyColumn && (
                          <p className="text-[11px] font-semibold text-[#0055a9] mt-0.5">
                            <CompanyCell tenantId={o.tenantId} />
                          </p>
                        )}
                      </div>
                      <div className="text-right shrink-0">
                        <StatusBadge status={o.status} />
                        <p className="text-base font-mono font-bold text-[#0E131B] mt-1.5 tabular-nums">{fmt(o.totalNetWeight)}<span className="text-[10px] text-[rgba(14,19,27,0.4)] ml-0.5">kg</span></p>
                      </div>
                      <svg className={`w-4 h-4 self-center shrink-0 text-[rgba(14,19,27,0.35)] transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {isOpen && (
                      <div className="border-t border-[#EEF0F3]">
                        {items.map((it: any) => (
                          <div key={it.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F4F5F8] last:border-b-0">
                            <span className="text-sm font-mono font-semibold text-[#0E131B] flex-1">{it.boxNo}</span>
                            <span className="text-sm font-mono text-[rgba(14,19,27,0.65)] tabular-nums">{fmt(it.netWeight)} kg</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
        </div>
      )}

      {/* ── APPROVALS (admin approves → all boxes deducted from stock at once) ── */}
      {tab === 'approvals' && canApprove && (
        <div className="space-y-3">
          {queueLoading ? <div className="p-10 text-center"><div className="animate-spin rounded-full h-7 w-7 border-2 border-[#0055a9] border-t-transparent mx-auto" /></div>
            : queue.length === 0 ? (
              <div className="bg-white rounded-xl border border-[#E8EBF1] p-10 text-center">
                <p className="text-sm font-bold text-emerald-700">Nothing waiting 🎉</p>
                <p className="text-xs text-[rgba(14,19,27,0.5)] mt-1">All outward entries are approved.</p>
              </div>
            ) : (
              <>
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-bold text-amber-800">{queue.length} {queue.length === 1 ? 'entry' : 'entries'} waiting on you</p>
                  <p className="text-xs text-amber-700/90 mt-0.5 leading-relaxed">Approving deducts these boxes from stock. Check the weight against the challan first.</p>
                </div>
                {queue.map((o) => {
                  const items = o.items || [];
                  return (
                    <div key={o.id} className="bg-white rounded-xl border border-[#E8EBF1] overflow-hidden">
                      <div className="px-4 py-3 border-b border-[#EEF0F3]">
                        <p className="text-base font-bold text-[#1B202A]">{o.buyerName}</p>
                        <p className="text-[11px] font-mono text-[rgba(14,19,27,0.5)] mt-0.5">
                          {o.challanDate || ''}{o.requester ? ` · by ${o.requester.name}` : ''}{o.challanNo ? ` · ${o.challanNo}` : ''}
                        </p>
                        {showCompanyColumn && (
                          <p className="text-[11px] font-semibold text-[#0055a9] mt-0.5">
                            <CompanyCell tenantId={o.tenantId} />
                          </p>
                        )}
                      </div>
                      <div className="grid grid-cols-2 border-b border-[#EEF0F3]">
                        <div className="px-4 py-3 border-r border-[#EEF0F3]">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Boxes</p>
                          <p className="text-xl font-mono font-bold text-[#0E131B] mt-0.5 tabular-nums">{items.length}</p>
                        </div>
                        <div className="px-4 py-3">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-[rgba(14,19,27,0.45)]">Net Wt</p>
                          <p className="text-xl font-mono font-bold text-[#0E131B] mt-0.5 tabular-nums">{fmt(o.totalNetWeight)}<span className="text-xs text-[rgba(14,19,27,0.4)] ml-1">kg</span></p>
                        </div>
                      </div>
                      <div className="max-h-52 overflow-y-auto">
                        {items.map((it: any) => (
                          <div key={it.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F4F5F8]">
                            <span className="text-sm font-mono font-semibold text-[#0055a9] flex-1">{it.boxNo}</span>
                            <span className="text-sm font-mono font-semibold text-[#0E131B] tabular-nums">{fmt(it.netWeight)} kg</span>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-[1fr_1.4fr] gap-2 p-3">
                        <button onClick={() => setConfirm({ o, action: 'reject' })} disabled={review.isPending}
                          className="h-14 rounded-xl border-2 border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 disabled:opacity-50 transition-colors">
                          Reject
                        </button>
                        <button onClick={() => setConfirm({ o, action: 'approve' })} disabled={review.isPending}
                          className="h-14 rounded-xl bg-[#0055a9] text-white hover:bg-[#0d2780] disabled:opacity-50 transition-colors flex flex-col items-center justify-center leading-tight">
                          <span className="text-sm font-bold">Approve &amp; deduct</span>
                          <span className="text-[10px] font-mono text-white/80 mt-0.5">{fmt(o.totalNetWeight)} kg out of stock</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
        </div>
      )}

      {/* Confirm dialog — required before an irreversible deduct (or a reject). */}
      {confirm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => !review.isPending && setConfirm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-[#E1E6EF] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 pt-5 pb-4 flex gap-3">
              <div className={`shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${confirm.action === 'approve' ? 'bg-[#EEF3FA] text-[#0055a9]' : 'bg-red-50 text-red-600'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" /></svg>
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-[#1B202A]">
                  {confirm.action === 'approve' ? 'Approve & deduct stock?' : 'Reject this request?'}
                </h3>
                <p className="text-sm text-[rgba(14,19,27,0.6)] mt-1">
                  {confirm.action === 'approve' ? (
                    <>This will deduct <span className="font-semibold text-[#0E131B]">{fmt(confirm.o.totalNetWeight)} kg</span> across <span className="font-semibold text-[#0E131B]">{(confirm.o.items || []).length} box(es)</span> for <span className="font-semibold text-[#0E131B]">{confirm.o.buyerName}</span> from stock. This can’t be undone.</>
                  ) : (
                    <>The request for <span className="font-semibold text-[#0E131B]">{confirm.o.buyerName}</span> will be rejected. No stock is deducted.</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 bg-[#F8F9FC] border-t border-[#E8EBF1]">
              <button onClick={() => setConfirm(null)} disabled={review.isPending}
                className="px-4 py-2 text-sm font-semibold text-[#0E131B] bg-white border border-[#E1E6EF] rounded-xl hover:bg-[#F1F3F9] disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={() => review.mutate({ id: confirm.o.id, action: confirm.action }, { onSuccess: () => setConfirm(null) })}
                disabled={review.isPending}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 ${confirm.action === 'approve' ? 'bg-[#0055a9] hover:bg-[#0d2780]' : 'bg-red-600 hover:bg-red-700'}`}>
                {review.isPending ? 'Working…' : (confirm.action === 'approve' ? 'Approve & deduct' : 'Reject request')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </motion.div>
  );
};

export default YarnOutward;
